import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { getChatHistory } from '@/api/agent'
import { isApiError } from '@/api/client'
import { NewBusinessDialog } from '@/components/business/NewBusinessDialog'
import { RenameBusinessDialog } from '@/components/business/RenameBusinessDialog'
import { DeleteBusinessDialog } from '@/pages/settings/business/BusinessParts'
import { useAuthStore } from '@/stores/useAuthStore'
import { useMyBusinessStore, type Business } from '@/stores/useMyBusinessStore'
import { useUiStore } from '@/stores/useUiStore'
import { BusinessNode } from './BusinessNode'

/** The Agents segment: every business the owner has as a collapsible row that opens its
 * Marketing chat, its team roles and groups under it, then "+ New business". Opening anything
 * in a business that isn't active switches to it, so Pages follow. */
export function AgentList() {
  const navigate = useNavigate()
  const activeId = useAuthStore((state) => state.user?.default_business_id)
  const businesses = useMyBusinessStore((state) => state.businesses)
  const switchBusiness = useMyBusinessStore((state) => state.switchBusiness)
  const createBusiness = useMyBusinessStore((state) => state.createBusiness)
  const updateBusiness = useMyBusinessStore((state) => state.updateBusiness)
  const deleteBusiness = useMyBusinessStore((state) => state.deleteBusiness)
  const showToast = useUiStore((state) => state.showToast)
  const [creating, setCreating] = useState(false)
  const [renaming, setRenaming] = useState<Business | null>(null)
  const [deleting, setDeleting] = useState<Business | null>(null)
  const history = useQuery({
    queryKey: ['agent-chat-history'],
    queryFn: () => getChatHistory(5),
    refetchInterval: 60_000,
    retry: false,
  })

  // A stale pointer resolves to the first business, as the backend does.
  const active = businesses.find((b) => b.id === activeId) ?? businesses[0]

  const openIn = (business: Business, path: string) => {
    if (business.id === active?.id) {
      navigate(path)
      return
    }
    switchBusiness(business.id, path).catch((error: unknown) =>
      showToast({
        type: 'error',
        title: 'Could not switch business',
        message: isApiError(error) ? error.message : undefined,
      })
    )
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteBusiness(deleting.id)
      showToast({
        type: 'success',
        title: 'Business deleted',
        message: `"${deleting.name}" was removed.`,
      })
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Could not delete business',
        message: isApiError(error) ? error.message : undefined,
      })
      throw error
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto" data-testid="thread-list">
      {businesses.map((business) => (
        <BusinessNode
          key={business.id}
          business={business}
          isActive={business.id === active?.id}
          preview={business.id === active?.id ? history.data?.messages.at(-1)?.content : undefined}
          onOpen={(path) => openIn(business, path)}
          onRename={() => setRenaming(business)}
          onDelete={() => setDeleting(business)}
        />
      ))}
      <button
        type="button"
        onClick={() => setCreating(true)}
        data-testid="agents-new-business"
        className="text-muted-foreground hover:bg-sidebar-accent/60 flex min-h-11 items-center gap-2.5 rounded-xl px-3 text-sm md:min-h-9"
      >
        <Plus className="size-4" />
        New business
      </button>

      <NewBusinessDialog open={creating} onOpenChange={setCreating} onSubmit={createBusiness} />
      <RenameBusinessDialog
        open={renaming !== null}
        onOpenChange={(open) => !open && setRenaming(null)}
        currentName={renaming?.name ?? ''}
        onSubmit={(name) => (renaming ? updateBusiness(renaming.id, { name }) : Promise.resolve())}
      />
      <DeleteBusinessDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        businessName={deleting?.name ?? ''}
        onConfirm={handleDelete}
      />
    </div>
  )
}
